import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ChevronDownIcon from '../../../assets/icons/chevron-down.svg?react';
import Pagination from '../../../components/Pagination/Pagination';
import './StationAccordion.css';

// 개별 충전소 행 컴포넌트
const StationRow = ({ station, stationNumber, isExpanded, onToggle, onChargerSelect }) => {
  const navigate = useNavigate();
  const getStatusInfo = (status) => {
    switch (status) {
      case 'using':
        return { text: '사용', color: '#29D668', dotColor: '#29D668', bgColor: '#E5FAEA' };
      case 'waiting':
        return { text: '대기', color: '#4FC3F7', dotColor: '#4FC3F7', bgColor: '#EDF9FE' };
      case 'unavailable':
        return { text: '불가', color: '#FEC02E', dotColor: '#FEC02E', bgColor: '#FFFDE7' };
      default:
        return { text: '대기', color: '#4FC3F7', dotColor: '#4FC3F7', bgColor: '#EDF9FE' };
    }
  };
  // 충전기 상태코드를 3가지 카테고리로 매핑하는 헬퍼 함수
  const getChargerStatusCategory = (statusCode) => {
    switch (statusCode) {
      case 2: // 충전대기
      case 6: // 예약중
        return 'waiting';
      case 3: // 충전중
        return 'using';
      case 0: // 알수없음
      case 1: // 통신이상
      case 4: // 운영중지
      case 5: // 점검중
      case 9: // 상태미확인
      default:
        return 'unavailable';
    }
  };
  const charger = station.Chargers;
  return (
    <React.Fragment>
      {/* 충전소 행 */}
      <tr 
        className="station-row primary-table-item-clickable"
        onClick={() => onToggle(station.StationSysId)}
      >
        <td className={`station-accordion-chevron chevron ${isExpanded ? 'expanded' : ''}`}>
          <ChevronDownIcon color='var(--gray-600)' width={20} height={20} />
        </td>
        <td className="station-name">
          <span>
            {station.CpoStationName || '충전소 이름 없음'}
          </span>
        </td>
        <td className="station-id">{station.CpoStationId || '-'}</td>
        {/* <td className="station-capacity">-</td>
        <td className="station-operation">-</td> */}
        <td className="station-address">{station.Address || '-'}</td>
        <td className="station-contract">
          {station.PowerMeterContractNumber ? (
            <div className="contract-numbers">
              {station.PowerMeterContractNumber.split(',').map((number, index) => (
                <div key={index} className="contract-number-item">
                  {number.trim()}
                </div>
              ))}
            </div>
          ) : (
            '-'
          )}
        </td>
        <td className="station-button">
          <p className="text-gray-600 cursor-pointer text-underline" onClick={(e) => {
            e.stopPropagation();
            navigate(`/station-management/detail/${station.StationSysId}`, { state: { from: 'detail' } });
          }}>자세히보기</p>
        </td>
      </tr>
      
      {/* 충전기 행들 (확장된 경우에만 표시) */}
      {isExpanded && (
        <>
          {charger.length > 0 ? ( 
            charger.map((charger, chargerIndex) => {
              const statusInfo = getStatusInfo(getChargerStatusCategory(charger.Status) || 'waiting');
              
              return (
                <tr 
                  key={charger.ChargerSysId}
                  className="charger-row primary-table-item-clickable"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChargerSelect && onChargerSelect(charger, station);
                  }}
                >
                  <td className="charger-chevron"></td>
                  <td className="charger-name">{station.CpoStationId}-{charger.CpoChargerId || '00'}</td>
                  <td className="charger-id">{charger.CpoChargerId || '-'}</td>
                  <td className="charger-address">{charger.ChargerCapacity || '-'}</td>
                  <td className="charger-operation">
                    <div className="status-indicator" style={{ backgroundColor: statusInfo.bgColor }}>
                      <span
                        className="status-dot" 
                        style={{ backgroundColor: statusInfo.dotColor }}
                      ></span>
                      <span className="status-text" style={{ color: statusInfo.color }}>
                        {statusInfo.text}
                      </span>
                    </div>
                  </td>
                  <td className="charger-button" onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/charger-management/detail/${charger.ChargerSysId}`, { state: { from: 'detail' } });
                  }}>
                    <p className="text-gray-600 cursor-pointer text-underline">자세히보기</p>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr className="charger-empty-row">
              <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
                충전기가 없습니다.
              </td>
            </tr>
          )}
        </>
      )}
    </React.Fragment>
  );
};

const StationAccordion = ({ stations = [], onChargerSelect }) => {
  const [expandedStations, setExpandedStations] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const toggleStation = (stationId) => {
    const newExpanded = new Set(expandedStations);
    if (newExpanded.has(stationId)) {
      newExpanded.delete(stationId);
    } else {
      newExpanded.add(stationId);
    }
    setExpandedStations(newExpanded);
  };

  // 페이징된 충전소 데이터 계산
  const paginatedStations = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return stations.slice(startIndex, endIndex);
  }, [stations, currentPage, itemsPerPage]);

  // 총 페이지 수 계산
  const totalPages = Math.ceil(stations.length / itemsPerPage);

  // 페이지 변경 핸들러
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // 페이지 변경 시 모든 아코디언 닫기 (선택사항)
    setExpandedStations(new Set());
  };

  return (
    <div className="station-accordion">
      <div className="primary-table">
        <table>
          <thead>
            <tr>
              <th width="32px"></th>
              <th>이름</th>
              <th width="80px">식별자</th>
              {/* <th></th>
              <th></th> */}
              <th>주소</th>
              <th width="120px">계량기 계약번호</th>
              <th width="100px"></th>
            </tr>
          </thead>
          <tbody>
            {paginatedStations.map((station, stationIndex) => {
              const isExpanded = expandedStations.has(station.StationSysId);
              // 전체 목록에서의 실제 번호 계산
              const actualStationNumber = (currentPage - 1) * itemsPerPage + stationIndex + 1;
              
              return (
                <StationRow
                  key={station.StationSysId}
                  station={station}
                  stationNumber={actualStationNumber}
                  isExpanded={isExpanded}
                  onToggle={toggleStation}
                  onChargerSelect={onChargerSelect}
                />
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="station-pagination">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            maxVisiblePages={10}
            size="medium"
          />
        </div>
      )}
    </div>
  );
};

export default StationAccordion;
